import React, { useCallback } from 'react';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { CreateUserForm } from '../admin/components/users/CreateUserForm.tsx';
import type { CreateUserRequest } from '../admin/types.ts';
import { createOrgMemberUser } from '../request/membership/orgUsers.api.ts';

function getStoredOrganizationId(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('organization_id') ?? '';
}

const OrgCreateUserPage: React.FC = () => {
  const organizationId = getStoredOrganizationId();

  const createUserRequest = useCallback(async (payload: CreateUserRequest) => {
    try {
      await createOrgMemberUser(payload);
      toast.success('User created successfully');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to create user';
      toast.error(message);
      throw error;
    }
  }, []);

  if (!organizationId) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-12 text-sm text-gray-600">
        Select an organization to manage users.
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
            <UserPlus className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create User</h1>
            <p className="text-sm text-gray-600">
              Add a new member to your organization with an assigned role.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <CreateUserForm organizationId={organizationId} createUserRequest={createUserRequest} />
      </div>
    </div>
  );
};

export default OrgCreateUserPage;
